import '../styles/JoinSession.css';

import React, {
  useEffect,
  useState,
} from 'react';

import {
  addDoc,
  collection,
  getDocs,
  getFirestore,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import {
  useLocation,
  useNavigate,
} from 'react-router-dom';

import firebase from '../firebase';
import {
  Button,
  Card,
  Container,
  Input,
  Typography,
} from './common';

export default function JoinSession() {
  const [code, setCode] = useState('');
  const [coachName, setCoachName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  const db = getFirestore(firebase);
  
  // Check for code and redirect parameters in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const codeParam = params.get('code');
    const redirectSessionId = params.get('redirect');
    
    if (codeParam) {
      setCode(codeParam.toUpperCase());
    }
    
    if (redirectSessionId) {
      // If we have a redirect parameter, check if we already have a session in localStorage
      const storedSessionId = localStorage.getItem('sessionId');
      const storedCoachName = localStorage.getItem('coachName');
      
      if (storedSessionId && storedCoachName) {
        // If we already have a session, redirect to it
        navigate(`/session/${storedSessionId}`);
      }
    }
  }, [location, navigate]);

  async function handleJoinSession(e: React.FormEvent) {
    e.preventDefault();
    
    if (!code.trim() || !coachName.trim()) {
      setError('Please enter both code and name');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Find session by code
      const sessionsRef = collection(db, 'sessions');
      const q = query(sessionsRef, where('code', '==', code.toUpperCase()));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        setError('Invalid session code');
        return;
      }
      
      const sessionDoc = querySnapshot.docs[0];
      const sessionId = sessionDoc.id;
      
      // Check if coach already exists in this session
      const coachesRef = collection(db, `sessions/${sessionId}/coaches`);
      const coachQuery = query(coachesRef, where('name', '==', coachName));
      const coachSnapshot = await getDocs(coachQuery);
      
      if (coachSnapshot.empty) {
        // Add coach to session's coaches collection
        await addDoc(coachesRef, {
          name: coachName,
          joinedAt: serverTimestamp()
        });
      }
      
      // Store session ID and coach name in localStorage
      localStorage.setItem('sessionId', sessionId);
      localStorage.setItem('coachName', coachName);
      localStorage.setItem('sessionCode', sessionDoc.data().code);
      
      setSessionId(sessionId);
      setJoined(true);
      
      // Check if we have a redirect parameter
      const params = new URLSearchParams(location.search);
      const redirectSessionId = params.get('redirect');
      
      if (redirectSessionId && redirectSessionId === sessionDoc.id) {
        // If the redirect parameter matches the session ID, redirect to the session
        navigate(`/session/${sessionDoc.id}`);
      }
      
    } catch (error) {
      setError('Failed to join session');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  if (joined && sessionId) {
    return (
      <Container>
        <Card>
          <div className="success-icon">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white"/>
            </svg>
          </div>
          
          <Typography variant="h2" align="center">Successfully Joined!</Typography>
          
          <Card variant="success">
            <Typography variant="h3" color="success">Welcome, {coachName}!</Typography>
            <Typography color="success">You have joined the session successfully.</Typography>
            <Button 
              variant="success" 
              fullWidth 
              onClick={() => navigate(`/session/${sessionId}`)}
            >
              Go to Session
            </Button>
          </Card>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        <Typography variant="h2" align="center">Join Coaching Session</Typography>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        <form onSubmit={handleJoinSession}>
          <Input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            label="Session Code"
            placeholder="Enter 6-digit code"
            required
            autoCapitalize="characters"
          />
          
          <Input
            type="text"
            value={coachName}
            onChange={(e) => setCoachName(e.target.value)}
            label="Your Name"
            placeholder="Enter your name"
            required
          />
          
          <Button 
            type="submit"
            disabled={loading}
            fullWidth
          >
            {loading ? 'Joining...' : 'Join Session'}
          </Button>
        </form>
      </Card>
    </Container>
  );
}